/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Tooltip,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useNavigate } from "react-router-dom";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import HistoryIcon from "@mui/icons-material/History";

import ContentShell from "components/ContentShell";
import CustomButton from "components/CustomButton";
import SearchInput from "components/SearchInput";

import { useSnackbar } from "hooks/useSnackbar";

import configData from "config/config.json";
import sessionHelper from "services/sessionHelper";
import { PermittedRoles } from "utils/permissions";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import {
  listPortalUsers,
  listPortalUserAuditLogs,
  deletePortalUser,
  unlockPortalUser,
} from "services/portalUserService";
import { UAParser } from "ua-parser-js";

const useStyles = makeStyles(() => ({
  container: {
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
  },
  activeChip: {
    backgroundColor: "#4caf50",
    color: "white",
  },
  inactiveChip: {
    backgroundColor: "#f44336",
    color: "white",
  },
  tableHeader: {
    backgroundColor: "#f5f5f5",
    fontWeight: "bold",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "300px",
  },
  auditActionChip: {
    fontSize: "11px",
    height: "22px",
    fontWeight: 600,
    letterSpacing: "0.3px",
  },
  auditRow: {
    "&:nth-of-type(odd)": {
      backgroundColor: "#fafafa",
    },
  },
}));

const PortalUsers = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalUsers, setTotalUsers] = useState(0);
  const [search, setSearch] = useState("");

  const [auditModalOpen, setAuditModalOpen] = useState(false);
  const [auditUser, setAuditUser] = useState<any>(null);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditPage, setAuditPage] = useState(0);
  const [auditRowsPerPage, setAuditRowsPerPage] = useState(25);
  const [auditTotal, setAuditTotal] = useState(0);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<any>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const hasCreatePermission = sessionHelper.hasRole(
    PermittedRoles.PORTAL_USER?.CREATE_ROLES,
  );
  const hasEditPermission = sessionHelper.hasRole(
    PermittedRoles.PORTAL_USER?.UPDATE_ROLES,
  );
  const hasAuditLogPermission = sessionHelper.hasRole(
    PermittedRoles.PORTAL_USER?.AUDIT_LOG_ROLES,
  );
  const hasDeletePermission = sessionHelper.hasRole(
    PermittedRoles.PORTAL_USER?.DELETE_ROLES,
  );
  const hasUnlockPermission = sessionHelper.hasRole(
    PermittedRoles.PORTAL_USER?.UNLOCK_ROLES,
  );

  const fetchUsers = async (pageNum = 0, pageSize = 25) => {
    try {
      setLoading(true);
      const response = await listPortalUsers({
        pageNo: pageNum + 1,
        pageSize: pageSize,
      });

      if (response?.status && response?.data) {
        setUsers(response.data.data || []);
        setTotalUsers(response.data.total || 0);
      }
    } catch (error: any) {
      console.error("Error fetching portal users:", error);
      showSnackbar(error?.message || "Failed to fetch portal users", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLogs = async (userId: string, pageNum = 0, pageSize = 25) => {
    try {
      setAuditLoading(true);
      const response = await listPortalUserAuditLogs(userId, {
        pageNo: pageNum + 1,
        pageSize: pageSize,
      });
      if (response?.status && response?.data) {
        setAuditLogs(response.data.data || []);
        setAuditTotal(response.data.total || 0);
      }
    } catch (error: any) {
      showSnackbar(error?.message || "Failed to fetch audit logs", "error");
    } finally {
      setAuditLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(page, rowsPerPage);
  }, []);

  const handleSearchChange = (value: any) => {
    setSearch(value);
  };

  const handleChangePage = (_event: any, newPage: number) => {
    setPage(newPage);
    fetchUsers(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchUsers(0, newRowsPerPage);
  };

  const handleEditUser = (userId: string) => {
    navigate(
      (configData.routes as any).portalUsers.edit?.replace(":id", userId),
    );
  };

  const handleCreateUser = () => {
    navigate((configData.routes as any).portalUsers.create);
  };

  const handleOpenDeleteDialog = (user: any) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    try {
      setDeleteLoading(true);
      const response = await deletePortalUser(userToDelete._id);
      if (response?.status) {
        showSnackbar("Portal user deleted successfully", "success");
        handleCloseDeleteDialog();
        fetchUsers(page, rowsPerPage);
      } else {
        showSnackbar(
          response?.message || "Failed to delete portal user",
          "error",
        );
      }
    } catch (error: any) {
      showSnackbar(error?.message || "Failed to delete portal user", "error");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleOpenAuditLogs = (user: any) => {
    setAuditUser(user);
    setAuditPage(0);
    setAuditRowsPerPage(25);
    setAuditLogs([]);
    setAuditTotal(0);
    setAuditModalOpen(true);
    fetchAuditLogs(user._id, 0, 25);
  };

  const handleCloseAuditLogs = () => {
    setAuditModalOpen(false);
    setAuditUser(null);
    setAuditLogs([]);
  };

  const handleAuditPageChange = (_event: any, newPage: number) => {
    setAuditPage(newPage);
    fetchAuditLogs(auditUser._id, newPage, auditRowsPerPage);
  };

  const handleAuditRowsPerPageChange = (event: any) => {
    const newSize = parseInt(event.target.value, 10);
    setAuditRowsPerPage(newSize);
    setAuditPage(0);
    fetchAuditLogs(auditUser._id, 0, newSize);
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return "-";
    }
  };

  const getActionColor = (
    action: string,
  ):
    | "default"
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning" => {
    if (action === "LOGIN") return "success";
    if (action.startsWith("VIEW")) return "info";
    if (action.startsWith("EXPORT")) return "warning";
    return "default";
  };

  const parseUserAgent = (uaString: string) => {
    if (!uaString) return "-";
    const result = UAParser(uaString);
    const parts: string[] = [];
    if (result.browser.name)
      parts.push(
        `${result.browser.name}${result.browser.version ? ` ${result.browser.version.split(".")[0]}` : ""}`,
      );
    if (result.os.name)
      parts.push(
        `${result.os.name}${result.os.version ? ` ${result.os.version}` : ""}`,
      );
    if (result.device.type)
      parts.push(
        result.device.type.charAt(0).toUpperCase() +
          result.device.type.slice(1),
      );
    return parts.length ? parts.join(" - ") : uaString.substring(0, 60);
  };

  // Client-side filtering by search
  const filteredUsers = search
    ? users.filter(
        (user: any) =>
          user.name?.toLowerCase().includes(search.toLowerCase()) ||
          user.email?.toLowerCase().includes(search.toLowerCase()),
      )
    : users;

  const handleUnlockUser = async (user: any) => {
    try {
      const response = await unlockPortalUser(user._id);
      if (response?.status) {
        showSnackbar(`Account unlocked for ${user.name}`, "success");
        fetchUsers(page, rowsPerPage);
      } else {
        showSnackbar(response?.message || "Failed to unlock account", "error");
      }
    } catch (error: any) {
      showSnackbar(error?.message || "Failed to unlock account", "error");
    }
  };

  const isLocked = (user: any) =>
    user.lockedUntil && new Date() < new Date(user.lockedUntil);

  const colSpan =
    hasEditPermission || hasAuditLogPermission || hasDeletePermission ? 7 : 6;

  return (
    <>
      <ContentShell
        title="Portal Users"
        loading={loading}
        actions={
          <Box style={{ display: "flex", flexFlow: "row nowrap", gap: 4 }}>
            <SearchInput
              value={search}
              placeholder="Search by name or email"
              onSearch={handleSearchChange}
            />
            {hasCreatePermission && (
              <CustomButton onClick={handleCreateUser}>
                Create Portal User
              </CustomButton>
            )}
          </Box>
        }
      >
        <Box className={classes.container}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow className={classes.tableHeader}>
                  <TableCell className={classes.tableHeader}>Name</TableCell>
                  <TableCell className={classes.tableHeader}>Email</TableCell>
                  <TableCell className={classes.tableHeader}>
                    Operators
                  </TableCell>
                  <TableCell className={classes.tableHeader}>
                    Last Login
                  </TableCell>
                  <TableCell className={classes.tableHeader} align="center">
                    Status
                  </TableCell>
                  <TableCell className={classes.tableHeader} align="center">
                    Lock Status
                  </TableCell>
                  {(hasEditPermission ||
                    hasAuditLogPermission ||
                    hasDeletePermission) && (
                    <TableCell className={classes.tableHeader} align="center">
                      Actions
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={colSpan} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={colSpan} align="center">
                      No portal users found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user: any) => (
                    <TableRow key={user._id} hover>
                      <TableCell>{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        {user.operatorIds?.map((op: any) => (
                          <Chip
                            key={op._id}
                            label={op.operatorName}
                            size="small"
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        ))}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          style={{
                            fontSize: "13px",
                            color: user.lastLogin ? "#2d3748" : "#a0aec0",
                          }}
                        >
                          {user.lastLogin
                            ? formatDate(user.lastLogin)
                            : "Never"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={user.isActive ? "Active" : "Inactive"}
                          className={
                            user.isActive
                              ? classes.activeChip
                              : classes.inactiveChip
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {isLocked(user) ? (
                          <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                            <Chip
                              label="Locked"
                              size="small"
                              sx={{ backgroundColor: "#e53e3e", color: "white", fontWeight: 600 }}
                            />
                            <Typography variant="caption" color="textSecondary">
                              Until {formatDate(user.lockedUntil)}
                            </Typography>
                          </Box>
                        ) : (
                          <Chip
                            label="Unlocked"
                            size="small"
                            sx={{ backgroundColor: "#48bb78", color: "white", fontWeight: 600 }}
                          />
                        )}
                      </TableCell>
                      {(hasEditPermission ||
                        hasAuditLogPermission ||
                        hasDeletePermission) && (
                        <TableCell align="center">
                          <Box display="flex" justifyContent="center" gap={0.5}>
                            {hasEditPermission && (
                              <Tooltip title="Edit user">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditUser(user._id)}
                                  aria-label="edit portal user"
                                  color="primary"
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            {hasAuditLogPermission && (
                              <Tooltip title="View audit logs">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenAuditLogs(user)}
                                  aria-label="view audit logs"
                                  color="default"
                                >
                                  <HistoryIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            {hasDeletePermission && (
                              <Tooltip title="Delete user">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenDeleteDialog(user)}
                                  aria-label="delete portal user"
                                  color="error"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            {hasUnlockPermission && isLocked(user) && (
                              <Tooltip title="Unlock account">
                                <IconButton
                                  size="small"
                                  onClick={() => handleUnlockUser(user)}
                                  aria-label="unlock account"
                                  color="warning"
                                >
                                  <LockOpenIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={totalUsers}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        </Box>
      </ContentShell>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Portal User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to permanently delete{" "}
            <strong>{userToDelete?.name}</strong> ({userToDelete?.email})? This
            will also delete all their audit logs and exports. This action
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDeleteDialog}
            variant="outlined"
            disabled={deleteLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={deleteLoading}
          >
            {deleteLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={auditModalOpen}
        onClose={handleCloseAuditLogs}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              <Typography variant="h6" style={{ fontWeight: 700 }}>
                Audit Logs
              </Typography>
              {auditUser && (
                <Typography variant="body2" color="textSecondary">
                  {auditUser.name} ({auditUser.email})
                </Typography>
              )}
            </Box>
            <Typography variant="body2" color="textSecondary">
              {auditTotal} total records
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell style={{ fontWeight: 700 }}>Action</TableCell>
                  <TableCell style={{ fontWeight: 700 }}>Endpoint</TableCell>
                  <TableCell style={{ fontWeight: 700 }}>IP Address</TableCell>
                  <TableCell style={{ fontWeight: 700 }}>User Agent</TableCell>
                  <TableCell style={{ fontWeight: 700 }}>Date & Time</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {auditLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : auditLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No audit logs found
                    </TableCell>
                  </TableRow>
                ) : (
                  auditLogs.map((log: any) => (
                    <TableRow key={log._id} className={classes.auditRow} hover>
                      <TableCell>
                        <Chip
                          label={log.action}
                          size="small"
                          color={getActionColor(log.action)}
                          className={classes.auditActionChip}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          style={{
                            fontSize: "12px",
                            fontFamily: "monospace",
                            color: "#4a5568",
                          }}
                        >
                          {log.endpoint}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          style={{ fontSize: "13px" }}
                        >
                          {log.ip || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={log.userAgent || ""} placement="top">
                          <Typography
                            variant="body2"
                            style={{
                              fontSize: "12px",
                              color: "#4a5568",
                              cursor: "default",
                            }}
                          >
                            {parseUserAgent(log.userAgent)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          style={{ fontSize: "13px" }}
                        >
                          {formatDate(log.createdAt)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={auditTotal}
            rowsPerPage={auditRowsPerPage}
            page={auditPage}
            onPageChange={handleAuditPageChange}
            onRowsPerPageChange={handleAuditRowsPerPageChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAuditLogs} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PortalUsers;
